import React from 'react';
import styled from 'styled-components';
import en from '../../language/Eng.json';

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const FooterContainer = styled.footer`
  background-color: #48200cff;
  color: #fceed1;
  padding: 30px 4vw 20px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  letter-spacing: 0.5px;
  line-height: 1.6;
  text-transform: uppercase;
  width: 100%;
  box-sizing: border-box;
  
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  min-height: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
`;

const Title = styled.div`
  margin-bottom: 8px;
`;

const SubLinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LinkItem = styled.a`
  cursor: pointer;
  display: inline-block;
  width: fit-content;
`;

const ContactItem = styled.a`
  cursor: pointer;
  display: inline-block;
  width: fit-content;
  text-transform: lowercase;
`;

const PhoneItem = styled.a`
  cursor: pointer;
  display: inline-block;
  width: fit-content;
`;

const LeftBottomArea = styled.div`
  display: flex;
  gap: 15vw;
  align-items: flex-end;
  margin-top: 25px;
  
  @media (max-width: 768px) {
    gap: 40px;
    flex-wrap: wrap;
    margin-top: 20px;
  }
`;

const FollowUs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Icons = styled.div`
  display: flex;
  gap: 15px;
  cursor: pointer;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(238, 219, 165, 0.5);
  padding-bottom: 8px;
  margin-bottom: 25px;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: #eedba5;
  flex: 1;
  font-family: inherit;
  font-size: 11px;
  outline: none;
  letter-spacing: 0.5px;
  &::placeholder {
    color: rgba(238, 219, 165, 0.5);
  }
`;

const SubscribeBtn = styled.button`
  background: transparent;
  border: none;
  color: #eedba5;
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  letter-spacing: 1px;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.7;
  }
`;

const RightBottomArea = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 769px) {
    padding-right: clamp(120px, 18vw, 220px);
  }
`;

const AddressEmailPhone = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddressItem = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  &:hover {
    text-decoration: underline;
  }
`;

const BigLogo = styled.img`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: clamp(60px, 12vw, 150px);
  width: auto;
  object-fit: contain;
  object-position: right center;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    align-self: flex-end;
    margin-top: 30px;
  }
`;

const OpeningHoursWrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
`;

const ToggleArrow = styled.div<{ $isOpen: boolean }>`
  font-size: 8px;
  color: rgba(252, 238, 209, 0.5);
  margin-top: 5px;
  transition: all 0.4s ease;
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: flex;
  align-items: center;
  
  &:hover {
    color: #fceed1;
  }
`;

const HoursDetail = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => (props.$isOpen ? '400px' : '0')};
  opacity: ${props => (props.$isOpen ? '1' : '0')};
  overflow: hidden;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 10px;
  color: rgba(252, 238, 209, 0.7);
  line-height: 1.6;
  white-space: pre-line;
  margin-top: ${props => (props.$isOpen ? '12px' : '0')};
  text-transform: none;
  letter-spacing: 0.3px;
`;

const Bottom: React.FC = () => {
  const data = en.bottom;
  const [showHoursDetail, setShowHoursDetail] = React.useState(false);

  const handlePhoneClick = (e: React.MouseEvent, phone: string) => {
    e.preventDefault();
    if (isMobile()) {
      window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
    } else {
      navigator.clipboard.writeText(phone).then(() => {
        alert('복사되었습니다');
      }).catch(() => {
        alert('복사되었습니다');
      });
    }
  };

  const handleEmailClick = (e: React.MouseEvent, email: string) => {
    e.preventDefault();
    if (isMobile()) {
      window.location.href = `mailto:${email}`;
    } else {
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`, '_blank');
    }
  };

  const handleAddressClick = (e: React.MouseEvent, address: string) => {
    e.preventDefault();
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.replace(/\\n/g, ' '))}`, '_blank');
  };

  return (
    <FooterContainer>
      <ContentGrid>
        <LeftCol>
          <div>
            <Title>{data.sitemapTitle}</Title>
            <SubLinkList>
              {data.sitemapLinksTop.map((link, idx) => (
                <LinkItem key={idx}>{link}</LinkItem>
              ))}
            </SubLinkList>
          </div>

          <LeftBottomArea>
            <FollowUs>
              <Title style={{ marginBottom: 10 }}>{data.followUsTitle}</Title>
              <Icons>
                <InstagramIcon />
                <FacebookIcon />
              </Icons>
            </FollowUs>
          </LeftBottomArea>
        </LeftCol>

        <RightCol>
          <RightBottomArea>
            <InfoBlock>
              <div>
                <div style={{ marginBottom: 8 }}>{data.openingTimesTitle}</div>
                <OpeningHoursWrapper onClick={() => setShowHoursDetail(!showHoursDetail)}>
                  <div style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}>
                    {data.openingTimesShort}
                  </div>
                  <ToggleArrow $isOpen={showHoursDetail}>▼</ToggleArrow>
                  <HoursDetail $isOpen={showHoursDetail}>
                    {data.openingTimesLong}
                  </HoursDetail>
                </OpeningHoursWrapper>
              </div>

              <AddressEmailPhone>
                <AddressItem onClick={(e) => handleAddressClick(e, data.address)}>
                  {data.address.split('\n').map((line, idx) => (
                    <div key={idx} style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}>{line.trim()}</div>
                  ))}
                </AddressItem>
                <ContactItem onClick={(e) => handleEmailClick(e, data.email)}>
                  {data.email}
                </ContactItem>
                <PhoneItem onClick={(e) => handlePhoneClick(e, data.phone)}>
                  {data.phone}
                </PhoneItem>
              </AddressEmailPhone>
            </InfoBlock>
          </RightBottomArea>
          <BigLogo src="/image/logo/Joie_Logo_oulined4.webp" alt="Joie Logo" />
        </RightCol>
      </ContentGrid>
    </FooterContainer>
  );
};

export default Bottom;